<template>
  <el-card class="summary" shadow="never" v-loading="fetchingPerformanceRates">
    <template #header>
      <div class="card-header">
        <el-form inline size="small">
          <el-form-item label="统计月份">
            <el-date-picker
              v-model="selectedMonth"
              type="month"
              value-format="yyyy-MM"
              placeholder="选择月份"
              @change="onMonthChange"
              :picker-options="monthPickerOptions"
            />
          </el-form-item>
          <el-form-item label="统计周">
            <el-select v-model="selectedWeek" placeholder="选择周" style="width: 200px;" @change="onWeekChange">
              <el-option
                v-for="week in weekOptions"
                :key="week.value"
                :label="week.label"
                :value="week.value"
                :disabled="week.disabled"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="业务员" v-if="roleKind !== 'salesman' && roleKind !== 'customer_service'">
            <el-select v-model="userId" placeholder="请选择" filterable clearable>
              <el-option v-for="user in users" :key="user.id" :label="user.screen_name" :value="user.id" />
            </el-select>
          </el-form-item>
        </el-form>
        <el-button type="primary" size="small" @click="exportToExcel">导出</el-button>
      </div>
    </template>

    <div class="box-main">
      <el-card shadow="never">
        <ECharts
          ref="chart"
          :options="chartOption"
          class="chart"
          autoresize
        />
      </el-card>

      <el-card shadow="never">
        <el-table :data="tableData" border :show-header="false">
          <!-- <template v-if="performanceGoalTargets.includes('finish_target')">
            <el-table-column prop="name" label="" min-width="80" />
            <el-table-column prop="value1" label="" min-width="60" align="center" />
          </template> -->
          <template v-if="performanceGoalTargets.includes('sales_target')">
            <el-table-column prop="name2" label="" min-width="80" />
            <el-table-column prop="value2" label="" min-width="60" align="center" />
          </template>
          <!-- <template v-if="performanceGoalTargets.includes('shipping_target')">
            <el-table-column prop="name3" label="" min-width="80" />
            <el-table-column prop="value3" label="" min-width="60" align="center" />
          </template> -->
        </el-table>
      </el-card>
    </div>

    <br>
    <Details :details="performanceRates.details" />
  </el-card>
</template>

<script>
import { Vue, Component, Prop, Mixins } from 'vue-property-decorator';
import ECharts from 'vue-echarts';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/legend';
import 'echarts/lib/component/title';
import 'echarts/lib/chart/bar';
import moment from 'moment';
import Utils from '@/components/utils.vue';
import * as XLSX from 'xlsx';
import Details from './details.vue';

@Component({
  components: {
    ECharts,
    Details,
  },
  filters: {
    toLocaleString(value) {
      return Number(value).toLocaleString('zh-CN');
    }
  }
})
export default class Summary extends Mixins(Vue, Utils) {
  @Prop({ type: Array, required: true }) users;

  selectedMonth = '';
  selectedWeek = null;
  weekOptions = [];

  userId = '';
  performanceRates = {
    details: [],
    summary: {
      finish_target: '0.0',
      finish_amount: '0.0',
      sales_target: '0.0',
      sales_amount: '0.0',
    }
  }

  get startOn() {
    const selectedWeekOption = this.weekOptions.find(w => w.value === this.selectedWeek);
    return selectedWeekOption ? selectedWeekOption.start_on : '';
  }

  get endOn() {
    const selectedWeekOption = this.weekOptions.find(w => w.value === this.selectedWeek);
    return selectedWeekOption ? selectedWeekOption.end_on : '';
  }

  get monthPickerOptions() {
    return {};
  }

  get currentUser() {
    return this.users.find(user => user.id === this.userId);
  }

  get rankUserDetail() {
    let userId;
    if (['salesman', 'customer_service'].includes(this.roleKind)) {
      userId = this.$root.user.id;
    } else {
      userId = this.userId;
    }
    if (!userId) return null;
    return this.performanceRates.details.find(item => item.user_id === userId);
  }

  get performanceRatesSummary() {
    let summry = this.performanceRates.summary;
    if (this.userId) {
      summry = this.performanceRates.details.find(item => item.user_id === this.userId) || {};
    }
    const finishTarget = Number(summry.finish_target) || 0;
    const finishAmount = Number(summry.finish_amount) || 0;
    const salesTarget = Number(summry.sales_target) || 0;
    const salesAmount = Number(summry.sales_amount) || 0;
    const shippingTarget = Number(summry.shipping_target) || 0;
    const shippingAmount = Number(summry.shipping_amount) || 0;
    return {
      finish_target: finishTarget,
      finish_amount: finishAmount,
      sales_target: salesTarget,
      sales_amount: salesAmount,
      shipping_target: shippingTarget,
      shipping_amount: shippingAmount,
      sales_diff: salesTarget - salesAmount,
      finish_diff: finishTarget - finishAmount,
      shipping_diff: shippingTarget - shippingAmount,
      sales_percent: this.calcFinishPercent(salesAmount, salesTarget),
      finish_percent: this.calcFinishPercent(finishAmount, finishTarget),
      shipping_percent: this.calcFinishPercent(shippingAmount, shippingTarget),
    };
  }

  get tableData() {
    return [
      {
        name: '完结目标',
        value1: this.performanceRatesSummary.finish_target.toLocaleString('zh-CN'),
        name2: '开单目标',
        value2: this.performanceRatesSummary.sales_target.toLocaleString('zh-CN'),
        name3: '出货目标',
        value3: this.performanceRatesSummary.shipping_target.toLocaleString('zh-CN')
      },
      {
        name: '实际完结金额',
        value1: this.performanceRatesSummary.finish_amount.toLocaleString('zh-CN'),
        name2: '实际开单金额',
        value2: this.performanceRatesSummary.sales_amount.toLocaleString('zh-CN'),
        name3: '实际出货金额',
        value3: this.performanceRatesSummary.shipping_amount.toLocaleString('zh-CN')
      },
      {
        name: '完成率',
        value1: this.performanceRatesSummary.finish_percent,
        name2: '完成率',
        value2: this.performanceRatesSummary.sales_percent,
        name3: '完成率',
        value3: this.performanceRatesSummary.shipping_percent
      },
      {
        name: '目标差额',
        value1: this.performanceRatesSummary.finish_diff > 0 ? this.performanceRatesSummary.finish_diff.toLocaleString('zh-CN') : '已达成',
        name2: '目标差额',
        value2: this.performanceRatesSummary.sales_diff > 0 ? this.performanceRatesSummary.sales_diff.toLocaleString('zh-CN') : '已达成',
        name3: '目标差额',
        value3: this.performanceRatesSummary.shipping_diff > 0 ? this.performanceRatesSummary.shipping_diff.toLocaleString('zh-CN') : '已达成'
      },
      this.rankUserDetail && {
        name: '完结排名',
        value1: this.rankUserDetail.rank_by_finish_amount,
        name2: '开单排名',
        value2: this.rankUserDetail.rank_by_sales_amount,
        name3: '出货排名',
        value3: this.rankUserDetail.rank_by_shipping_amount
      }
    ].filter(Boolean);
  }

  fetchingPerformanceRates = false;
  request = null;

  calcFinishPercent(finishAmount, finishTarget) {
    return finishTarget ? (
      finishAmount ? `${((finishAmount / finishTarget) * 100).toFixed(2)}%` : '0%'
    ) : '-';
  }

  beforeDestroy() {
    if (this.request && this.request.engine) {
      this.request.engine.abort();
    }
  }

  created() {
    this.selectedMonth = moment().format('YYYY-MM');
    this.initCurrentWeek();
  }

  initCurrentWeek() {
    const today = moment();
    const dayOfMonth = today.date();
    const month = today.clone().startOf('month');

    // 获取当月1号是周几 (0=周日, 1=周一, ..., 6=周六)
    const firstDayOfWeek = month.day();

    // 计算第一周的结束日期（到周日）
    const firstSundayDate = firstDayOfWeek === 0 ? 1 : (7 - firstDayOfWeek + 1);

    // 根据当前日期判断属于第几周
    let currentWeekValue = 1;
    if (dayOfMonth <= firstSundayDate) {
      currentWeekValue = 1;
    } else {
      // 计算从第二周开始的周数
      const daysAfterFirstWeek = dayOfMonth - firstSundayDate;
      currentWeekValue = Math.ceil(daysAfterFirstWeek / 7) + 1;
    }

    // 生成当前月的周选项
    this.weekOptions = this.generateWeekOptions(this.selectedMonth);

    // 选择当前周，如果不存在则选择第一个可用的
    const availableWeek = this.weekOptions.find(w => w.value === currentWeekValue);
    this.selectedWeek = availableWeek ? currentWeekValue : (this.weekOptions[0] ? this.weekOptions[0].value : 1);

    // 自动执行一次查询
    this.$nextTick(() => {
      this.fetchPerformancesRates();
    });
  }

  generateWeekOptions(monthStr) {
    const month = moment(monthStr, 'YYYY-MM');
    const startOfMonth = month.clone().startOf('month');
    const endOfMonth = month.clone().endOf('month');
    const today = moment();

    // 获取当月1号是周几 (0=周日, 1=周一, ..., 6=周六)
    const firstDayOfWeek = startOfMonth.day();

    // 计算第一周的结束日期（到周日）
    // 如果1号是周日，那第一周就是1号这一天
    // 否则，第一周是从1号到最近的周日
    const firstSundayDate = firstDayOfWeek === 0 ? 1 : (7 - firstDayOfWeek + 1);

    const weekRanges = [];
    let currentStart = 1;
    let weekNumber = 1;

    // 第一周
    weekRanges.push({
      start: currentStart,
      end: firstSundayDate,
      label: `第${weekNumber}周`
    });

    // 后续周
    currentStart = firstSundayDate + 1;
    weekNumber++;

    while (currentStart <= endOfMonth.date()) {
      const weekEnd = Math.min(currentStart + 6, endOfMonth.date());
      weekRanges.push({
        start: currentStart,
        end: weekEnd,
        label: `第${weekNumber}周`
      });
      currentStart = weekEnd + 1;
      weekNumber++;
    }

    const options = [];

    weekRanges.forEach((range, index) => {
      const weekStart = startOfMonth.clone().date(range.start);
      const weekEnd = startOfMonth.clone().date(range.end);

      // 判断是否可选：当前时间大于等于周开始时间
      const isDisabled = today.isBefore(weekStart, 'day');

      options.push({
        label: `${range.label}(${weekStart.format('MMDD')}-${weekEnd.format('MMDD')})`,
        value: index + 1,
        start_on: weekStart.format('YYYY-MM-DD'),
        end_on: weekEnd.format('YYYY-MM-DD'),
        disabled: isDisabled
      });
    });

    return options;
  }


  onMonthChange() {
    if (!this.selectedMonth) return;

    this.weekOptions = this.generateWeekOptions(this.selectedMonth);

    // 月切换后使用第一周
    if (this.weekOptions.length > 0) {
      this.selectedWeek = this.weekOptions[0].value;
    }

    // 自动获取数据
    this.$nextTick(() => {
      this.fetchPerformancesRates();
    });
  }

  onWeekChange() {
    // 周切换后自动获取数据
    this.fetchPerformancesRates();
  }

  async fetchPerformancesRates() {
    if (!this.startOn || !this.endOn) return;
    this.fetchingPerformanceRates = true;
    try {
      if (this.request && this.request.engine) {
        this.request.engine.abort();
      }

      this.request = this.$request.get('/reports/sales_follows/performance_rates', {
        start_on: this.startOn,
        end_on: this.endOn,
      }, { timeout: 3 * 60 * 1000, loading: false });
      const data = await this.request;
      this.performanceRates = data;
    } finally {
      this.fetchingPerformanceRates = false;
    }
  }

  get chartOption() {
    const data = [
      {
        name: '开单',
        if: this.performanceGoalTargets.includes('sales_target'),
        target: this.performanceRatesSummary.sales_target,
        amount: {
          value: this.performanceRatesSummary.sales_amount,
          label: {
            show: true,
            position: 'right',
            fontSize: 10,
            formatter: () => {
              return [
                `完成率: ${this.performanceRatesSummary.sales_percent}`,
                this.performanceRatesSummary.sales_diff > 0 ? `还差: ${this.performanceRatesSummary.sales_diff}` : '已达成'
              ].filter(Boolean).join('\n');
            }
          }
        },
      },
      // {
      //   name: '完结',
      //   if: this.performanceGoalTargets.includes('finish_target'),
      //   target: this.performanceRatesSummary.finish_target,
      //   amount: {
      //     value: this.performanceRatesSummary.finish_amount,
      //     label: {
      //       show: true,
      //       position: 'right',
      //       fontSize: 10,
      //       formatter: () => {
      //         return [
      //           `完成率: ${this.performanceRatesSummary.finish_percent}`,
      //           this.performanceRatesSummary.finish_diff > 0 ? `还差: ${this.performanceRatesSummary.finish_diff}` : '已达成'
      //         ].filter(Boolean).join('\n');
      //       }
      //     }
      //   },
      // },
      // {
      //   name: '出货',
      //   if: this.performanceGoalTargets.includes('shipping_target'),
      //   target: this.performanceRatesSummary.shipping_target,
      //   amount: {
      //     value: this.performanceRatesSummary.shipping_amount,
      //     label: {
      //       show: true,
      //       position: 'right',
      //       fontSize: 10,
      //       formatter: () => {
      //         return [
      //           `完成率: ${this.performanceRatesSummary.shipping_percent}`,
      //           this.performanceRatesSummary.shipping_diff > 0 ? `还差: ${this.performanceRatesSummary.shipping_diff}` : '已达成'
      //         ].filter(Boolean).join('\n');
      //       }
      //     }
      //   }
      // },
    ].filter(item => item.if);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {
        data: ['目标', '实际金额']
      },
      grid: {
        left: '3%',
        right: '15%',
        bottom: '10%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        name: '金额',
        axisLabel: {
          formatter: (value) => {
            return (value / 10000).toFixed(1) + 'w';
          }
        }
      },
      yAxis: {
        type: 'category',
        data: data.map(item => item.name)
      },
      series: [
        {
          name: '目标',
          type: 'bar',
          barWidth: 30,
          itemStyle: {
            color: '#101ebf'
          },
          data: data.map(item => item.target)
        },
        {
          name: '实际金额',
          type: 'bar',
          barWidth: 30,
          itemStyle: {
            color: '#67c23a'
          },
          data: data.map(item => item.amount)
        }
      ]
    };
  }

  exportToExcel() {
    // 只导出开单相关数据
    // const visibleKeys = ['业务员', '开单目标', '实际开单金额', '开单完成率', '开单差额'];

    const exportData = this.performanceRates.details.map(item => {
      const salesDiff = Number(item.sales_target) - Number(item.sales_amount);
      return {
        '业务员': item.screen_name,
        '开单目标': Number(item.sales_target),
        '实际开单金额': Number(item.sales_amount),
        '开单完成率': Number(item.sales_target) === 0 ? '-' :
          `${((Number(item.sales_amount) / Number(item.sales_target)) * 100).toFixed(2)}%`,
        '开单差额': salesDiff > 0 ? salesDiff : '已达成',
      };
    });

    // 添加合计行
    exportData.push({
      '业务员': '合计',
      '开单目标': this.performanceRatesSummary.sales_target,
      '实际开单金额': this.performanceRatesSummary.sales_amount,
      '开单完成率': this.performanceRatesSummary.sales_percent,
      '开单差额': this.performanceRatesSummary.sales_diff > 0 ? this.performanceRatesSummary.sales_diff : '已达成',
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '业绩目标统计');

    // 生成文件名
    const selectedWeekOption = this.weekOptions.find(w => w.value === this.selectedWeek);
    const weekLabel = selectedWeekOption ? selectedWeekOption.label.replace(/[()]/g, '') : '';
    const fileName = `业绩目标统计_${this.selectedMonth}_${weekLabel}.xlsx`;

    // 导出文件
    XLSX.writeFile(wb, fileName);
  }
}
</script>

<style lang="scss" scoped>
.summary {
  flex: 1;
  display: flex;
  flex-direction: column;

  /deep/.el-card__header {
    flex-shrink: 0;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  /deep/.el-card__body {
    flex: 1;
    overflow: auto;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .el-form {
      flex: 1;
    }

    .el-button {
      margin-left: 16px;
    }

    .el-form-item {
      margin-bottom: 0;
    }
  }

  .box-main {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
  }

  .chart {
    width: 100%;
    height: 400px;
  }

  .el-card {
    flex: 1;
  }
}

.rank-row {
  line-height: 1.6;

  .rank-value {
    font-weight: bold;
  }
}
</style>
